import React, { useState } from "react";
import { useCart } from "../../context/CartContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "../../context/TranslationsProvider";
import Header from "./components/Header";
import Footer from "./components/Footer";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);
const API_URL = process.env.REACT_APP_API_URL;


const Cart: React.FC = () => {
    const { cart, removeFromCart } = useCart();
    const { lang } = useParams<{ lang: string }>();
    const navigate = useNavigate();
    const { translate } = useTranslation();
    const [confirmingRemovalId, setConfirmingRemovalId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const totalPrice = cart.reduce((sum, item) => {
        const price = parseFloat(String(item.price).replace(/[^0-9.]/g, ""));
        return sum + price;
    }, 0);


    const handleCheckout = async () => {
        setIsLoading(true);
        const stripe = await stripePromise;
        if (!stripe) return;

        try {
            const cartItemsWithFullImageUrls = cart.map((item) => ({
                ...item,
                image: item.image,
            }));

            const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ cartItems: cartItemsWithFullImageUrls }),
            });

            if (!response.ok) throw new Error("Failed to create checkout session");

            const data = await response.json();
            if (!data.sessionId) throw new Error("Invalid session ID");
            await stripe.redirectToCheckout({ sessionId: data.sessionId });

        } catch (error) {
            setIsLoading(false)
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className="flex flex-col min-h-screen bg-[#f4f1ea]">
            <Header />

            <div className="flex-grow flex flex-col items-center p-4 md:p-10">
                <div className="container max-w-6xl flex flex-grow w-full">
                    <div className="flex flex-grow flex-col space-y-4 lg:space-y-0 lg:flex-row lg:space-x-4">
                        <div className="w-full lg:w-3/4 flex flex-col flex-grow bg-white p-4 md:p-6 shadow-md min-h-64">
                            <div className="flex justify-between border-b">
                                <h2 className="text-xl font-bold pb-2 mb-4">{translate("cart_name")}</h2>
                                <h2>{cart.length} {translate("cart_total_items")}</h2>
                            </div>
                            <div className="flex flex-col flex-grow">
                                {cart.length > 0 ? (
                                    cart.map((item) => (
                                        <div
                                            key={item.id}
                                            onClick={() => navigate(`/painting/${item.title}`)}
                                            className="cursor-pointer hover:bg-gray-100 transition-colors flex flex-col sm:flex-row sm:items-center justify-between border-b py-4 mb-4"
                                        >
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-32 h-32 sm:w-28 sm:h-28 object-cover mb-4 sm:mb-0 mx-auto sm:mx-0"
                                            />

                                            <p className="text-lg text-center sm:text-left sm:w-48 w-full overflow-hidden whitespace-nowrap overflow-ellipsis sm:ml-4">
                                                {item.title}
                                            </p>

                                            <p className="text-lg w-full sm:w-20 text-center sm:text-right mt-2 sm:mt-0 sm:ml-4">
                                                {item.price}€
                                            </p>

                                            <div onClick={(e) => e.stopPropagation()} className="text-gray-600 flex flex-col items-center sm:items-end sm:ml-4 mt-2 sm:mt-0">
                                                {confirmingRemovalId !== item.id ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setConfirmingRemovalId(item.id);
                                                        }}
                                                        className="cursor-pointer hover:bg-gray-200 text-sm px-4 py-2 rounded-md w-full text-center"
                                                    >
                                                        {translate("cart_remove")}
                                                    </button>
                                                ) : (
                                                    <div className="flex flex-col items-end">
                                                        <div className="flex gap-2 mt-1">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeFromCart(item.id);
                                                                    setConfirmingRemovalId(null);
                                                                }}
                                                                className="px-2 py-1 bg-gray-500 text-orange-100 rounded text-sm"
                                                            >
                                                                {translate("cart_yes")}
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setConfirmingRemovalId(null);
                                                                }}
                                                                className="px-2 py-1 bg-black text-orange-100 rounded text-sm"
                                                            >
                                                                {translate("cart_no")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="h-32 flex items-center justify-center text-gray-400">
                                        {translate("cart_empty")}
                                    </div>
                                )}
                            </div>
                            <Link to="/all-paintings" className="flex items-center text-black hover:underline mt-4"> ← {translate("continue_shopping")}</Link>
                        </div>

                        <div className="bg-white w-full lg:w-1/4 p-4 md:p-6 shadow-md h-64 flex flex-col justify-between">
                            <div className="flex flex-col flex-grow">
                                <h1 className="text-xl font-bold border-b pb-6 mb-4">{translate("cart_summary")}</h1>
                                <div className="flex flex-grow flex-col justify-between">
                                    <div className="flex justify-between item-center">
                                        <h2 className="text-lg font-semibold">{translate("cart_total_items")}</h2>
                                        <p className="text-lg"> {cart.length}</p>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-semibold">{translate("cart_total_price")}</h2>
                                        <p className="text-md"> {totalPrice.toFixed(2)} €</p>
                                    </div>
                                </div>
                            </div>

                            <button
                                className={`mt-4 w-full px-4 py-2 rounded-md transition ${cart.length > 0 ? "bg-black text-orange-100" : "bg-gray-400 text-gray-200 cursor-not-allowed"}`}
                                onClick={handleCheckout}
                                disabled={cart.length === 0 || isLoading}
                            >
                                {isLoading ? (
                                    <div className="w-6 h-6 border-4 border-t-orange-100 border-gray-600 rounded-full animate-spin mx-auto"></div>
                                ) : (
                                    translate("cart_checkout")
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Cart;
